import {
  closestCenter,
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useMemo } from 'react';
import { IAchievementModal } from '../AchievementsModalTypes';
import { isGamePassAchievement, findGamePassIndex } from '../utils';

export function useAchievementsDragAndDrop(
  values: IAchievementModal[],
  setValues: (updater: (items: IAchievementModal[]) => IAchievementModal[]) => void,
) {
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    keyboardSensor,
  );

  const customCollisionDetection: CollisionDetection = useMemo(() => (args) => {
    const { droppableContainers } = args;
    const gamePassIndex = findGamePassIndex(values);

    // Filter out droppable containers that are above GamePass (if GamePass exists)
    const filteredContainers = droppableContainers.filter((container) => {
      const containerIndex = values.findIndex((item) => item.id === container.id);

      // Allow dropping anywhere if no GamePass exists
      if (gamePassIndex === -1) return true;

      // Don't allow dropping above GamePass
      return containerIndex > gamePassIndex;
    });

    // Use closestCenter collision detection on filtered containers
    return closestCenter({
      ...args,
      droppableContainers: filteredContainers,
    });
  }, [values]);

  const handleDragStart = useMemo(() => (event: DragStartEvent) => {
    const { active } = event;
    const activeItem = values.find((item) => item.id === active.id);

    // Prevent dragging GamePass achievement
    if (activeItem && isGamePassAchievement(activeItem)) {
      return false; // Cancel the drag operation
    }

    return true; // Allow the drag operation
  }, [values]);

  const handleDragEnd = useMemo(() => (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const gamePassIndex = findGamePassIndex(values);
      const overIndex = values.findIndex((item) => item.id === over.id);

      // Prevent dropping above GamePass achievement (if GamePass exists)
      if (gamePassIndex !== -1 && overIndex <= gamePassIndex) {
        return; // Cancel the move operation
      }

      setValues((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems;
      });
    }
  }, [values, setValues]);

  return {
    sensors,
    collisionDetection: customCollisionDetection,
    handleDragStart,
    handleDragEnd,
  };
}
